import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InputUnit, PantryRow } from '../../../types';
import { INPUT_UNITS } from '../../../types';
import { Plus, X, Icon } from '../../ui/Icon';
import { knownInputUnit, parseAmount } from './pantryUnits';
import QuantityEditor from './QuantityEditor';

export interface IngredientActions {
  products: PantryRow[];
  onAdd: (mealId: string, name: string, amount: number, unit: InputUnit) => Promise<boolean>;
  onEdit: (mealId: string, ingredientId: string, amount: number, unit: InputUnit) => Promise<boolean>;
  onRemove: (mealId: string, ingredientId: string) => void;
}

/** A chip: an ingredient already saved, or one staged in the add form. */
export interface IngredientChip {
  key: string;
  name: string;
  label: string;
  /** What the editor opens with, as the user would type it. */
  typed: { amount: string; unit: InputUnit };
}

interface Props {
  items: IngredientChip[];
  products: PantryRow[];
  /** Resolves false to keep what was typed (a unit clash, a failed save). */
  onAdd: (name: string, amount: number, unit: InputUnit) => Promise<boolean>;
  onEdit: (key: string, amount: number, unit: InputUnit) => Promise<boolean>;
  onRemove: (key: string) => void;
}

/**
 * What a meal spends of each product (ADR-0014). Optional: a meal with none
 * spends nothing and the shopping list behaves as before. The same editor
 * serves a saved meal and one still being written in the add form.
 */
export default function MealIngredients({ items, products, onAdd, onEdit, onRemove }: Props) {
  const { t } = useTranslation();
  const listId = useId();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<InputUnit>('u');
  const [editing, setEditing] = useState<string | null>(null);

  function changeName(value: string) {
    setName(value);
    const known = knownInputUnit(products, value);
    if (known) setUnit(known);
  }

  async function submit() {
    const value = parseAmount(amount);
    if (!name.trim() || value === null) return;
    if (await onAdd(name.trim(), value, unit)) {
      setName('');
      setAmount('');
      setUnit('u');
    }
  }

  return (
    <div className="space-y-1.5">
      <p className="font-mono text-[10px] font-medium tracking-[0.12em] uppercase text-slate-400 dark:text-slate-500">
        {t('meals.ingredients.title')}
      </p>

      {items.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {items.map(i => (
            <li
              key={i.key}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] bg-teal-50 text-teal-800 dark:bg-teal-900/25 dark:text-teal-200"
            >
              {i.name}
              {editing === i.key ? (
                <QuantityEditor
                  amount={i.typed.amount}
                  unit={i.typed.unit}
                  onSave={async (value, u) => {
                    const ok = await onEdit(i.key, value, u);
                    if (ok) setEditing(null);
                    return ok;
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                // The amount is the button: tapping what you want to change is
                // the one gesture nobody has to be taught.
                <button
                  onClick={() => setEditing(i.key)}
                  className="font-mono tabular-nums text-teal-600 dark:text-teal-400 underline decoration-dotted decoration-teal-300 underline-offset-2 hover:text-teal-800 dark:hover:text-teal-200 cursor-pointer"
                  aria-label={t('meals.ingredients.editAmount', { name: i.name })}
                >
                  {i.label}
                </button>
              )}
              <button
                onClick={() => onRemove(i.key)}
                className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-teal-100 dark:hover:bg-teal-900/50 cursor-pointer"
                aria-label={t('meals.ingredients.remove', { name: i.name })}
              >
                <Icon as={X} size={10} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* A name left typed is an ingredient: leaving the row adds it, so "Done"
          or "Save" never drops what is in the box. Moving between its own
          fields (name → amount → unit) is not leaving it. */}
      <div
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void submit(); }}
        className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-teal-500/40 focus-within:border-teal-500"
      >
        <input
          value={name}
          onChange={e => changeName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          list={listId}
          placeholder={t('meals.ingredients.namePlaceholder')}
          className="flex-1 min-w-0 bg-transparent pl-2.5 pr-1 py-1.5 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none"
        />
        <datalist id={listId}>
          {products.map(p => <option key={p.id} value={p.name} />)}
        </datalist>
        <input
          value={amount}
          onChange={e => setAmount(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          inputMode="decimal"
          placeholder="1"
          aria-label={t('meals.ingredients.amountLabel')}
          className="w-12 shrink-0 bg-transparent border-l border-slate-100 dark:border-slate-700 px-2 py-1.5 text-xs font-mono tabular-nums text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
        />
        <select
          value={unit}
          onChange={e => setUnit(e.target.value as InputUnit)}
          aria-label={t('meals.ingredients.unit')}
          className="shrink-0 bg-transparent border-l border-slate-100 dark:border-slate-700 px-1.5 py-1.5 text-xs text-slate-600 dark:text-slate-300 focus:outline-none cursor-pointer"
        >
          {INPUT_UNITS.map(u => <option key={u} value={u}>{t(`meals.units.${u}`)}</option>)}
        </select>
        <button
          onClick={submit}
          disabled={!name.trim()}
          className="shrink-0 m-0.5 w-6 h-6 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center disabled:opacity-30 disabled:cursor-default cursor-pointer"
          aria-label={t('meals.ingredients.add')}
        >
          <Icon as={Plus} size={12} />
        </button>
      </div>
    </div>
  );
}
